import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { NotificationManager } from './Notification';
import '../styles/CardForm.css';

const CardForm = ({ onSave, initialData = null }) => {
  const navigate = useNavigate();
  const photoInputRef = useRef(null);
  const logoInputRef = useRef(null);
  const [activeTab, setActiveTab] = useState('basic');
  
  const [formData, setFormData] = useState(initialData || {
    id: '',
    firstName: '',
    lastName: '',
    title: '',
    company: '',
    companyLogo: '',
    email: '',
    phone: '',
    website: '',
    address: '',
    note: '',
    photoUrl: '',
    themeColor: '#3498db',
    socials: {
      linkedin: '',
      twitter: '',
      instagram: '',
      facebook: '',
      youtube: '',
      skype: '',
      whatsapp: '',
      other: ''
    }
  });

  const [photoPreview, setPhotoPreview] = useState(formData.photoUrl || '');
  const [logoPreview, setLogoPreview] = useState(formData.companyLogo || '');

  useEffect(() => {
    if (initialData) {
      setPhotoPreview(initialData.photoUrl || '');
      setLogoPreview(initialData.companyLogo || '');
    }
  }, [initialData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.includes('.')) {
      // Pour les champs de réseaux sociaux
      const [parent, child] = name.split('.');
      setFormData({
        ...formData,
        [parent]: {
          ...formData[parent],
          [child]: value
        }
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
        setFormData({
          ...formData,
          photoUrl: reader.result
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
        setFormData({
          ...formData,
          companyLogo: reader.result
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validation basique
    if (!formData.firstName && !formData.lastName) {
      NotificationManager.show('Erreur', 'Veuillez saisir au moins un nom ou un prénom', 'error');
      return;
    }
    
    // Générer un ID si c'est une nouvelle carte
    if (!formData.id) {
      const newId = `${formData.firstName.toLowerCase()}-${formData.lastName.toLowerCase()}-${Date.now()}`;
      formData.id = newId;
    }
    
    onSave(formData);
    NotificationManager.show('Succès', initialData ? 'Carte mise à jour avec succès' : 'Carte créée avec succès', 'success');
    navigate('/');
  };

  const handleReset = () => {
    if (initialData) {
      setFormData(initialData);
      setPhotoPreview(initialData.photoUrl || '');
      setLogoPreview(initialData.companyLogo || '');
    } else {
      setFormData({
        id: '',
        firstName: '',
        lastName: '',
        title: '',
        company: '',
        companyLogo: '',
        email: '',
        phone: '',
        website: '',
        address: '',
        note: '',
        photoUrl: '',
        themeColor: '#3498db',
        socials: {
          linkedin: '',
          twitter: '',
          instagram: '',
          facebook: '',
          youtube: '',
          skype: '',
          whatsapp: '',
          other: ''
        }
      });
      setPhotoPreview('');
      setLogoPreview('');
    }
  };

  return (
    <div className="container">
      <div className="main-content">
        <h1>{initialData ? 'Modifier' : 'Ajouter'} une carte</h1>
        
        <div className="tabs">
          <div 
            className={`tab ${activeTab === 'basic' ? 'active' : ''}`} 
            onClick={() => setActiveTab('basic')}
          >
            Informations de base
          </div>
          <div 
            className={`tab ${activeTab === 'social' ? 'active' : ''}`} 
            onClick={() => setActiveTab('social')}
          >
            Réseaux sociaux
          </div>
        </div>
        
        <form onSubmit={handleSubmit}>
          <div className={`tab-content ${activeTab === 'basic' ? 'active' : ''}`} id="basic-tab">
            <div className="upload-area" onClick={() => photoInputRef.current.click()}>
              <div className="preview-image" id="photoPreview">
                {photoPreview ? (
                  <img src={photoPreview} alt="Aperçu du profil" />
                ) : (
                  <div className="photo-placeholder">
                    <div className="photo-icon">👤</div>
                    <div className="photo-text">Photo</div>
                  </div>
                )}
              </div>
              <input
                type="file"
                ref={photoInputRef}
                onChange={handlePhotoChange}
                accept="image/*"
                className="file-input"
              />
              <p>Cliquez pour ajouter une photo</p>
            </div>
            
            <div className="form-group">
              <label htmlFor="firstName">Prénom</label>
              <input 
                type="text" 
                id="firstName"
                name="firstName" 
                value={formData.firstName} 
                onChange={handleChange} 
                placeholder="Prénom"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="lastName">Nom</label>
              <input 
                type="text" 
                id="lastName"
                name="lastName" 
                value={formData.lastName} 
                onChange={handleChange} 
                placeholder="Nom de famille"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="title">Poste</label>
              <input 
                type="text" 
                id="title"
                name="title" 
                value={formData.title} 
                onChange={handleChange} 
                placeholder="Titre ou fonction"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="company">Entreprise</label>
              <input 
                type="text" 
                id="company"
                name="company" 
                value={formData.company} 
                onChange={handleChange} 
                placeholder="Nom de l'entreprise"
              />
            </div>
            
            <div className="upload-area" onClick={() => logoInputRef.current.click()}>
              <div className="preview-image" id="logoPreview">
                {logoPreview ? (
                  <img src={logoPreview} alt="Logo entreprise" />
                ) : (
                  <div className="photo-placeholder">
                    <div className="photo-icon">🏢</div>
                    <div className="photo-text">Logo</div>
                  </div>
                )}
              </div>
              <input
                type="file"
                ref={logoInputRef}
                onChange={handleLogoChange}
                accept="image/*"
                className="file-input"
              />
              <p>Cliquez pour ajouter un logo d'entreprise</p>
            </div>
            
            <div className="form-group">
              <label htmlFor="phone">Téléphone</label>
              <input 
                type="tel" 
                id="phone"
                name="phone" 
                value={formData.phone} 
                onChange={handleChange} 
                placeholder="Ex: +41791234567"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input 
                type="email" 
                id="email"
                name="email" 
                value={formData.email} 
                onChange={handleChange}
                placeholder="adresse@email.com" 
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="website">Site Web</label>
              <input 
                type="url" 
                id="website"
                name="website" 
                value={formData.website} 
                onChange={handleChange} 
                placeholder="https://www.example.com"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="address">Adresse</label>
              <textarea 
                id="address"
                name="address" 
                value={formData.address} 
                onChange={handleChange} 
                placeholder="Adresse complète"
                rows="3"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="note">Notes</label>
              <textarea 
                id="note"
                name="note" 
                value={formData.note} 
                onChange={handleChange}
                placeholder="Informations supplémentaires"
                rows="2" 
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="themeColor">Couleur du Thème</label>
              <input 
                type="color" 
                id="themeColor"
                name="themeColor" 
                value={formData.themeColor} 
                onChange={handleChange} 
              />
            </div>
          </div>
          
          <div className={`tab-content ${activeTab === 'social' ? 'active' : ''}`} id="social-tab">
            <h2>Réseaux sociaux</h2>
            <p style={{ marginBottom: '15px' }}>Ajoutez vos identifiants sur les réseaux sociaux</p>
            
            <div className="social-media-container">
              <div className="social-input-group">
                <div className="social-icon-label">📱</div>
                <input 
                  type="text" 
                  id="socialLinkedin"
                  name="socials.linkedin" 
                  value={formData.socials.linkedin} 
                  onChange={handleChange}
                  placeholder="URL LinkedIn" 
                />
              </div>
              
              <div className="social-input-group">
                <div className="social-icon-label">🐦</div>
                <input 
                  type="text" 
                  id="socialTwitter"
                  name="socials.twitter" 
                  value={formData.socials.twitter} 
                  onChange={handleChange}
                  placeholder="Identifiant Twitter/X" 
                />
              </div>
            </div>
            
            <div className="social-media-container" style={{ marginTop: '10px' }}>
              <div className="social-input-group">
                <div className="social-icon-label">📷</div>
                <input 
                  type="text" 
                  id="socialInstagram"
                  name="socials.instagram" 
                  value={formData.socials.instagram} 
                  onChange={handleChange}
                  placeholder="Identifiant Instagram" 
                />
              </div>
              
              <div className="social-input-group">
                <div className="social-icon-label">📘</div>
                <input 
                  type="text" 
                  id="socialFacebook"
                  name="socials.facebook" 
                  value={formData.socials.facebook} 
                  onChange={handleChange}
                  placeholder="URL Facebook" 
                />
              </div>
            </div>
            
            <div className="social-media-container" style={{ marginTop: '10px' }}>
              <div className="social-input-group">
                <div className="social-icon-label">📺</div>
                <input 
                  type="text" 
                  id="socialYoutube"
                  name="socials.youtube" 
                  value={formData.socials.youtube} 
                  onChange={handleChange}
                  placeholder="Chaîne YouTube" 
                />
              </div>
              
              <div className="social-input-group">
                <div className="social-icon-label">👥</div>
                <input 
                  type="text" 
                  id="socialSkype"
                  name="socials.skype" 
                  value={formData.socials.skype} 
                  onChange={handleChange}
                  placeholder="Identifiant Skype" 
                />
              </div>
            </div>
            
            <div className="social-media-container" style={{ marginTop: '10px' }}>
              <div className="social-input-group">
                <div className="social-icon-label">📱</div>
                <input 
                  type="text" 
                  id="socialWhatsapp"
                  name="socials.whatsapp" 
                  value={formData.socials.whatsapp} 
                  onChange={handleChange}
                  placeholder="Numéro WhatsApp" 
                />
              </div>
              
              <div className="social-input-group">
                <div className="social-icon-label">🔗</div>
                <input 
                  type="text" 
                  id="socialOther"
                  name="socials.other" 
                  value={formData.socials.other} 
                  onChange={handleChange}
                  placeholder="Autre réseau social" 
                />
              </div>
            </div>
          </div>
          
          <div className="actions">
            <button type="button" onClick={() => navigate('/')} className="btn-danger">
              Annuler
            </button>
            <button type="button" onClick={handleReset} className="btn-primary">
              Réinitialiser
            </button>
            <button type="submit" className="btn-success">
              {initialData ? 'Mettre à jour' : 'Créer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CardForm;
